import {
	getVariableInfo,
	getVariableToken,
} from '@dealstack/frontend/shared/components/EmailTemplates/utils/emailVariableCatalog';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Text } from '@mantine/core';
import type { Editor } from '@tiptap/core';

import { useInspectorPresets } from '../../hooks/useInspectorPresets';
import { BRAND_PRESET_COLORS, withCustomColors } from '../../utils/inspectorColors';
import { closestPresetValue } from '../../utils/inspectorPresets';
import { effectiveStyleValue } from '../../utils/effectiveStyle';
import { readInlineStyle, toggleInlineMark, writeInlineStyle } from '../../utils/inlineTextStyle';
import { ColorSwatchRow } from './ColorSwatchRow';
import { FormatRow } from './FormatRow';
import { InspectorSection } from './InspectorSection';
import { PresetRow } from './PresetRow';

interface Props {
	editor: Editor;
	name: string;
	marks: Record<string, boolean>;
	customColors: string[];
	onCustomColorUsed: (hex: string) => void;
}

// Styled with marks, like text: the library's StyleAttribute extension only
// covers its own node types, so this node cannot hold a style of its own.
export const VariableProperties = ({
	editor,
	name,
	marks,
	customColors,
	onCustomColorUsed,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const presets = useInspectorPresets();
	const info = getVariableInfo(name);

	return (
		<>
			<InspectorSection
				title={tMipManage('email_templates.editor.inspector.variable', {
					defaultValue: 'Variable',
				})}
			>
				<div data-re-inspector-prop-row="">
					<label data-re-inspector-label="">
						{tMipManage('email_templates.editor.inspector.variable_value', {
							defaultValue: 'Value',
						})}
					</label>
					<Text fz="0.75rem" lh="1.125rem" style={{ textAlign: 'right' }}>
						{info.label}
					</Text>
				</div>
				<div data-re-inspector-prop-row="">
					<label data-re-inspector-label="">
						{tMipManage('email_templates.editor.inspector.variable_token', {
							defaultValue: 'Token',
						})}
					</label>
					<input
						data-re-inspector-input=""
						data-grow=""
						type="text"
						readOnly
						value={getVariableToken(name)}
						title={getVariableToken(name)}
						aria-label={tMipManage('email_templates.editor.inspector.variable_token', {
							defaultValue: 'Token',
						})}
					/>
				</div>
			</InspectorSection>

			<InspectorSection
				title={tMipManage('email_templates.editor.inspector.typography', {
					defaultValue: 'Typography',
				})}
			>
				<ColorSwatchRow
					label={tMipManage('email_templates.editor.inspector.color', {
						defaultValue: 'Color',
					})}
					value={readInlineStyle(editor, 'color') ?? effectiveStyleValue(editor, 'color')}
					presetColors={withCustomColors(BRAND_PRESET_COLORS, customColors)}
					onChange={(value) => writeInlineStyle(editor, 'color', value)}
					onCustomColorUsed={onCustomColorUsed}
				/>
				<PresetRow
					label={tMipManage('email_templates.editor.inspector.font_size', {
						defaultValue: 'Font size',
					})}
					options={presets.fontSize}
					value={closestPresetValue(
						presets.fontSize,
						readInlineStyle(editor, 'fontSize') ??
							effectiveStyleValue(editor, 'fontSize')
					)}
					onChange={(value) => writeInlineStyle(editor, 'fontSize', value)}
				/>
				<FormatRow marks={marks} onToggle={(mark) => toggleInlineMark(editor, mark)} />
			</InspectorSection>
		</>
	);
};
